import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supportApi, orgApi, getErrorMessage } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  LifeBuoy,
  Loader2,
  CheckCircle2,
  Clock,
  Building2,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
];

const AdminTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState({});
  const [allowed, setAllowed] = useState(null); // null = loading, true/false

  // Gate: caller must be admin in the default org (platform admin)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (user?.role !== 'admin') {
        if (alive) setAllowed(false);
        return;
      }
      try {
        const res = await orgApi.getMine();
        if (!alive) return;
        setAllowed(!!res.data?.is_default);
      } catch {
        if (alive) setAllowed(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user?.role]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportApi.listAll(filter === 'all' ? null : filter);
      setTickets(res.data?.items || []);
      setOpenCount(res.data?.open_count || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load tickets'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (allowed) fetchTickets();
  }, [allowed, fetchTickets]);

  const handleResolve = async (id) => {
    setResolving((s) => ({ ...s, [id]: true }));
    try {
      await supportApi.resolve(id);
      toast.success('Ticket marked resolved');
      fetchTickets();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not resolve ticket'));
    } finally {
      setResolving((s) => ({ ...s, [id]: false }));
    }
  };

  if (allowed === null) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!allowed) {
    return (
      <div
        className="max-w-xl mx-auto py-16 text-center"
        data-testid="admin-tickets-forbidden"
      >
        <p className="text-sm text-slate-500 mb-4">
          This page is only available to InterviewWala platform administrators.
        </p>
        <Link to="/dashboard">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1
            className="text-3xl font-bold text-slate-900 flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <LifeBuoy className="w-7 h-7 text-amber-600" /> Support Tickets
            <Badge
              className="ml-2 bg-amber-100 text-amber-800 border-amber-200"
              data-testid="admin-tickets-open-count"
            >
              {openCount} open
            </Badge>
          </h1>
          <p className="text-slate-600 mt-1">
            Every ticket submitted by every workspace, newest first.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-md border border-slate-200 bg-white p-0.5"
            role="tablist"
            data-testid="admin-tickets-filter"
          >
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 text-xs font-medium rounded-sm transition ${
                  filter === f.id
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`admin-tickets-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTickets}
            disabled={loading}
            data-testid="admin-tickets-refresh"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading tickets…
            </div>
          ) : tickets.length === 0 ? (
            <div
              className="text-center py-16 text-slate-400 text-sm"
              data-testid="admin-tickets-empty"
            >
              No tickets matching this filter.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {tickets.map((t) => (
                <li
                  key={t.id}
                  className="px-4 py-4 hover:bg-slate-50/50 transition"
                  data-testid={`admin-ticket-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
                        {t.message}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                          <Building2 className="w-3 h-3" />
                          {t.org_name || t.org_id || 'Unknown org'}
                        </span>
                        <span>·</span>
                        <span>{t.user_name}</span>
                        <span className="text-slate-400">
                          ({t.user_email})
                        </span>
                        {t.page_context && (
                          <>
                            <span>·</span>
                            <span className="font-mono px-1 py-0 rounded bg-slate-100">
                              {t.page_context}
                            </span>
                          </>
                        )}
                        <span>·</span>
                        <span>
                          {new Date(t.created_at).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <Badge
                        className={
                          t.status === 'resolved'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : 'bg-amber-100 text-amber-800 border-amber-200'
                        }
                      >
                        {t.status === 'resolved' ? (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        ) : (
                          <Clock className="w-3 h-3 mr-1" />
                        )}
                        {t.status}
                      </Badge>
                      {t.status === 'open' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolve(t.id)}
                          disabled={resolving[t.id]}
                          data-testid={`admin-ticket-resolve-${t.id}`}
                        >
                          {resolving[t.id] ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                          )}
                          Resolve
                        </Button>
                      )}
                      <a
                        href={`mailto:${t.user_email}?subject=${encodeURIComponent(
                          'Re: Your support request'
                        )}`}
                        className="text-[11px] text-teal-700 hover:underline"
                      >
                        Reply via email
                      </a>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTickets;
